/**
 * max-one-per-cart.js  (v5 — one per product family + one per Tonality trial)
 * --------------------------------------------------------------------------
 * A customer can have ToneHub Pro, ControlHub Pro and AmpHub All Access in
 * the cart together, but only ONE of each. Monthly and yearly (trial or paid)
 * of the same product count as the same product.
 *
 * Each Tonality free-trial variant is also limited to one per order.
 *
 * Shopify's /cart.js lists the most recently added item first, so when a
 * product is over the limit the NEWEST line is kept and older ones removed.
 *
 * This is a front-end guard. For guaranteed enforcement at checkout, add a
 * Cart & Checkout Validation Function (or an app that provides one).
 * --------------------------------------------------------------------------
 */
(function () {
  "use strict";

  /* ========================= CONFIG ========================= */

  // Each group is one "product". All IDs in a group share a limit of MAX_PER_GROUP.
  var PRODUCT_GROUPS = {
    "ToneHub Pro": [
      6848034275418, // ToneHub Pro - Monthly Subscription
      6848052887642, // ToneHub Pro - Annual Subscription
    ],
    "ControlHub Pro": [
      6844186591322, // ControlHub Pro - Monthly Subscription
      6847998754906, // ControlHub Pro - Annual Subscription
    ],
    "AmpHub All Access": [
      6847934464090, // AmpHub - Monthly Subscription
      6847934431322, // AmpHub - Annual Subscription
    ],
  };

  // Non-subscription free trials: each of these VARIANTS is limited to 1.
  var LIMITED_VARIANTS = {
    "Tonality - Howard Benson (Trial)": 39585770438746,
    "Tonality - Andy James (Trial)": 39326453989466,
    "Tonality - Will Putney (Trial)": 39326450188378,
    "Tonality - Lasse Lammert (Trial)": 39291446362202,
    "Tonality - Josh Middleton (Trial)": 40228208410714,
    "Tonality - Wes Borland (Trial)": 41380120690778,
  };

  var MAX_PER_GROUP = 1;

  /* ======================= END CONFIG ======================= */

  var num = function (v) { return parseInt(v, 10); };
  var rawFetch = window.fetch ? window.fetch.bind(window) : null;

  // Uses the unpatched fetch so our own requests don't re-trigger reconcile.
  function getCart() {
    return rawFetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); });
  }

  function applyChange(change) {
    return rawFetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: change.key, quantity: change.quantity }),
    }).then(function (r) { return r.json(); });
  }

  function groupOf(item) {
    var vid = num(item.variant_id);
    for (var vname in LIMITED_VARIANTS) {
      if (LIMITED_VARIANTS[vname] === vid) return vname;
    }
    var pid = num(item.product_id);
    for (var name in PRODUCT_GROUPS) {
      if (PRODUCT_GROUPS[name].indexOf(pid) !== -1) return name;
    }
    return null;
  }

  function planChanges(cart) {
    var remaining = {};
    var trimmed = [];
    var changes = [];

    cart.items.forEach(function (item) {
      var g = groupOf(item);
      if (!g) return;
      if (!(g in remaining)) remaining[g] = MAX_PER_GROUP;
      var keep = Math.min(item.quantity, remaining[g]);
      remaining[g] -= keep;
      if (keep !== item.quantity) {
        changes.push({ key: item.key, quantity: keep });
        if (trimmed.indexOf(g) === -1) trimmed.push(g);
      }
    });

    return { changes: changes, trimmed: trimmed };
  }

  // If a cart change happens while we're mid-reconcile, run again afterwards
  // instead of dropping it (v2 silently skipped these).
  var running = false;
  var runAgain = false;

  function reconcile() {
    if (!rawFetch) return;
    if (running) { runAgain = true; return; }
    running = true;

    getCart()
      .then(function (cart) {
        var plan = planChanges(cart);
        if (!plan.changes.length) return;
        return plan.changes
          .reduce(function (chain, change) {
            return chain.then(function () { return applyChange(change); });
          }, Promise.resolve())
          .then(function () {
            notifyUser("Some items are limited to one per order, so we've removed the extras: " +
              plan.trimmed.join(", ") + ".");
            refreshCartUI();
          });
      })
      .catch(function (e) { console.warn("[max-one-per-cart] reconcile failed:", e); })
      .then(function () {
        running = false;
        if (runAgain) { runAgain = false; reconcile(); }
      });
  }

  // Visible notice (v2 only fired an event that nothing listened for).
  function notifyUser(message) {
    document.dispatchEvent(new CustomEvent("max-one-per-cart:limited", { detail: { message: message } }));
    var toast = document.getElementById("max-one-per-cart-toast");
    if (!toast) {
      toast = document.createElement("div");
      toast.id = "max-one-per-cart-toast";
      toast.setAttribute("role", "status");
      toast.style.cssText =
        "position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:2147483647;" +
        "max-width:90vw;padding:14px 20px;border-radius:8px;background:#111;color:#fff;" +
        "font:14px/1.4 sans-serif;box-shadow:0 4px 16px rgba(0,0,0,.3);transition:opacity .3s;";
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.style.opacity = "1";
    clearTimeout(toast.__hideTimer);
    toast.__hideTimer = setTimeout(function () { toast.style.opacity = "0"; }, 6000);
  }

  function refreshCartUI() {
    getCart()
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent("theme:cart:change", { detail: { cart: cart }, bubbles: true }));
      })
      .catch(function () {});
    ["cart:refresh", "cart:updated", "cart-drawer:refresh"].forEach(function (evt) {
      document.dispatchEvent(new CustomEvent(evt, { bubbles: true }));
    });
    if (typeof window.updateCart === "function") {
      try { window.updateCart(); } catch (e) {}
    }
  }

  /* ---- Watch for cart mutations by patching fetch + XHR ---- */

  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?/i;

  // Handles string URLs, Request objects and URL objects.
  function urlOf(input) {
    if (!input) return "";
    if (typeof input === "string") return input;
    if (input.url) return String(input.url);
    return String(input.href || input);
  }

  if (rawFetch) {
    window.fetch = function (input) {
      var p = rawFetch.apply(null, arguments);
      if (CART_MUTATION.test(urlOf(input))) {
        p.then(function () { setTimeout(reconcile, 50); }, function () {});
      }
      return p;
    };
  }

  var origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__cartMutation = CART_MUTATION.test(urlOf(url));
    return origOpen.apply(this, arguments);
  };
  var origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    if (this.__cartMutation) {
      this.addEventListener("load", function () { setTimeout(reconcile, 50); });
    }
    return origSend.apply(this, arguments);
  };

  // Full-page adds, restored carts, back-button visits.
  function start() { reconcile(); }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
  window.addEventListener("pageshow", function (e) { if (e.persisted) reconcile(); });
})();
